import type { LevelDef, PieceKind, WorldDef } from '../core/types';
import { PIECE_KINDS } from '../core/types';

const BASIC: PieceKind[] = ['ember', 'tide', 'gale', 'root', 'solar'];
const SIX: PieceKind[] = ['ember', 'tide', 'gale', 'root', 'solar', 'moon'];
const ALL: PieceKind[] = [...PIECE_KINDS];

function L(
  partial: Omit<LevelDef, 'forbidInitialMatches'> & { forbidInitialMatches?: boolean }
): LevelDef {
  return { forbidInitialMatches: true, ...partial };
}

/** 50 handcrafted levels across 5 worlds. */
export const CAMPAIGN: LevelDef[] = [
  // ─── World 1: Ember Meadows — teach basics ───
  L({
    id: 1,
    worldId: 1,
    name: 'First Spark',
    moves: 20,
    rows: 7,
    cols: 7,
    pieceSet: BASIC.slice(0, 4),
    objectives: [{ type: 'score', target: 1500, label: 'Score 1,500' }],
    difficulty: 1,
    seed: 1001,
    tutorial: [
      'Swap adjacent essences to make a line of three or more.',
      'Matched essences clear, and new ones fall from above.',
      'Reach the score goal before moves run out.',
    ],
    starScores: [1500, 2500, 4000],
  }),
  L({
    id: 2,
    worldId: 1,
    name: 'Petal Practice',
    moves: 18,
    rows: 7,
    cols: 7,
    pieceSet: BASIC.slice(0, 4),
    objectives: [{ type: 'collect', target: 20, kind: 'ember', label: 'Collect 20 Ember Petals' }],
    difficulty: 1,
    seed: 1002,
    tutorial: ['Clear Ember Petals by matching them. Watch the counter in the HUD.'],
    starScores: [1200, 2200, 3500],
  }),
  L({
    id: 3,
    worldId: 1,
    name: 'Twin Goals',
    moves: 20,
    rows: 8,
    cols: 7,
    pieceSet: BASIC.slice(0, 5),
    objectives: [
      { type: 'collect', target: 15, kind: 'tide', label: 'Collect 15 Tide Coils' },
      { type: 'collect', target: 15, kind: 'gale', label: 'Collect 15 Gale Plumes' },
    ],
    difficulty: 1,
    seed: 1003,
    tutorial: ['Some looms require more than one essence. Balance both counters.'],
  }),
  L({
    id: 4,
    worldId: 1,
    name: 'Long Weave',
    moves: 18,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [
      { type: 'score', target: 3000, label: 'Score 3,000' },
      { type: 'activate_specials', target: 1, special: 'beam', label: 'Forge 1 Beam Spindle' },
    ],
    difficulty: 2,
    seed: 1004,
    tutorial: ['Match four in a line to forge a Beam Spindle. Swap it to clear a whole row or column.'],
  }),
  L({
    id: 5,
    worldId: 1,
    name: 'Square Burst',
    moves: 18,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [
      { type: 'activate_specials', target: 2, special: 'burst', label: 'Forge 2 Burst Cocoons' },
    ],
    difficulty: 2,
    seed: 1005,
    tutorial: ['A 2×2 block of the same essence forges a Burst Cocoon — a compact blast.'],
  }),
  L({
    id: 6,
    worldId: 1,
    name: 'Meadow Cascade',
    moves: 16,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [{ type: 'score', target: 5000, label: 'Score 5,000' }],
    difficulty: 2,
    seed: 1006,
  }),
  L({
    id: 7,
    worldId: 1,
    name: 'Frost Tips',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [{ type: 'clear_frost', target: 12, label: 'Melt 12 Frost Latches' }],
    layout: frostPatch(8, 8, [
      [2, 2], [2, 3], [2, 4], [3, 2], [3, 4], [4, 2], [4, 3], [4, 4],
      [5, 3], [3, 5], [5, 5], [6, 4],
    ], 1),
    difficulty: 2,
    seed: 1007,
    tutorial: ['Frost Latches melt when you match on top of them. Clear every layer.'],
  }),
  L({
    id: 8,
    worldId: 1,
    name: 'Irregular Grove',
    moves: 18,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [
      { type: 'score', target: 4000, label: 'Score 4,000' },
      { type: 'collect', target: 18, kind: 'root', label: 'Collect 18 Root Knots' },
    ],
    layout: holes(8, 8, [
      [0, 0], [0, 7], [7, 0], [7, 7], [3, 3], [3, 4], [4, 3], [4, 4],
    ]),
    difficulty: 2,
    seed: 1008,
    tutorial: ['Some cells are empty voids — pieces cannot rest there.'],
  }),
  L({
    id: 9,
    worldId: 1,
    name: 'Prism Lesson',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: BASIC,
    objectives: [
      { type: 'activate_specials', target: 1, special: 'prism', label: 'Forge 1 Prism Heart' },
      { type: 'score', target: 3500, label: 'Score 3,500' },
    ],
    difficulty: 3,
    seed: 1009,
    tutorial: ['Match five in a line to forge a Prism Heart. Swap it to clear that essence everywhere.'],
  }),
  L({
    id: 10,
    worldId: 1,
    name: 'Ember Warden',
    moves: 22,
    rows: 9,
    cols: 8,
    pieceSet: BASIC,
    objectives: [
      { type: 'score', target: 8000, label: 'Score 8,000' },
      { type: 'clear_frost', target: 16, label: 'Melt 16 Frost Latches' },
      { type: 'activate_specials', target: 3, label: 'Forge 3 Specials' },
    ],
    layout: frostPatch(9, 8, ringCoords(9, 8, 2), 2),
    difficulty: 3,
    seed: 1010,
  }),

  // ─── World 2: Tide Atelier — collect, drop, under-tiles ───
  L({
    id: 11,
    worldId: 2,
    name: 'Shallow Pools',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX.slice(0, 5),
    objectives: [{ type: 'clear_tiles', target: 20, label: 'Restore 20 Loom Tiles' }],
    layout: underTiles(8, 8, fullRect(1, 1, 6, 6), 1),
    difficulty: 2,
    seed: 2011,
    tutorial: ['Luminous Loom Tiles under the board clear when you match above them.'],
  }),
  L({
    id: 12,
    worldId: 2,
    name: 'Tide Collection',
    moves: 18,
    rows: 8,
    cols: 8,
    pieceSet: SIX.slice(0, 5),
    objectives: [
      { type: 'collect', target: 25, kind: 'tide', label: 'Collect 25 Tide Coils' },
      { type: 'clear_tiles', target: 12, label: 'Restore 12 Loom Tiles' },
    ],
    layout: underTiles(8, 8, checker(8, 8), 1),
    difficulty: 2,
    seed: 2012,
  }),
  L({
    id: 13,
    worldId: 2,
    name: 'Relic Sink',
    moves: 22,
    rows: 8,
    cols: 7,
    pieceSet: SIX.slice(0, 5),
    objectives: [{ type: 'drop', target: 3, label: 'Drop 3 Star Relics' }],
    prefill: relicTop(7, 3),
    difficulty: 2,
    seed: 2013,
    tutorial: ['Star Relics must fall to the bottom row. Clear the path beneath them.'],
  }),
  L({
    id: 14,
    worldId: 2,
    name: 'Layered Frost',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX.slice(0, 5),
    objectives: [{ type: 'clear_frost', target: 20, label: 'Melt 20 Frost layers' }],
    layout: frostPatch(8, 8, fullRect(2, 2, 5, 5), 2),
    difficulty: 3,
    seed: 2014,
  }),
  L({
    id: 15,
    worldId: 2,
    name: 'Nova Spark',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX.slice(0, 5),
    objectives: [
      { type: 'activate_specials', target: 2, special: 'nova', label: 'Forge 2 Nova Looms' },
    ],
    difficulty: 3,
    seed: 2015,
    tutorial: ['Intersecting matches (T or L shapes) forge a Nova Loom — a radial blast.'],
  }),
  L({
    id: 16,
    worldId: 2,
    name: 'Double Drop',
    moves: 24,
    rows: 9,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'drop', target: 4, label: 'Drop 4 Star Relics' },
      { type: 'score', target: 4000, label: 'Score 4,000' },
    ],
    prefill: relicTop(8, 4),
    difficulty: 3,
    seed: 2016,
  }),
  L({
    id: 17,
    worldId: 2,
    name: 'Crated Channels',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX.slice(0, 5),
    objectives: [{ type: 'clear_crate', target: 10, label: 'Break 10 Crate layers' }],
    layout: crates(8, 8, [
      [1, 3], [1, 4], [2, 3], [2, 4], [5, 3], [5, 4], [6, 3], [6, 4], [3, 1], [4, 6],
    ], 2),
    difficulty: 3,
    seed: 2017,
    tutorial: ['Wooden Crates block cells until you damage them with adjacent matches or specials.'],
  }),
  L({
    id: 18,
    worldId: 2,
    name: 'Moonlight Mix',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'collect', target: 18, kind: 'moon', label: 'Collect 18 Moon Chimes' },
      { type: 'clear_tiles', target: 16, label: 'Restore 16 Loom Tiles' },
    ],
    layout: underTiles(8, 8, fullRect(0, 0, 7, 7), 1),
    difficulty: 3,
    seed: 2018,
  }),
  L({
    id: 19,
    worldId: 2,
    name: 'Combo Study',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'combo_specials', target: 1, label: 'Combine 2 Specials once' },
      { type: 'score', target: 5000, label: 'Score 5,000' },
    ],
    difficulty: 3,
    seed: 2019,
    tutorial: ['Swap two specials into each other for a powerful combination effect.'],
  }),
  L({
    id: 20,
    worldId: 2,
    name: 'Tide Sovereign',
    moves: 24,
    rows: 9,
    cols: 9,
    pieceSet: SIX,
    objectives: [
      { type: 'drop', target: 3, label: 'Drop 3 Star Relics' },
      { type: 'clear_tiles', target: 24, label: 'Restore 24 Loom Tiles' },
      { type: 'clear_crate', target: 8, label: 'Break 8 Crate layers' },
    ],
    layout: {
      ...underTiles(9, 9, fullRect(1, 1, 7, 7), 1),
      ...crates(9, 9, [[2, 4], [3, 4], [4, 2], [4, 6], [5, 4], [6, 4], [4, 4], [1, 1]], 2),
    },
    prefill: relicTop(9, 3),
    difficulty: 4,
    seed: 2020,
  }),

  // ─── World 3: Gale Spires — portals, one-way, irregular ───
  L({
    id: 21,
    worldId: 3,
    name: 'Wind Gap',
    moves: 18,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [{ type: 'score', target: 4500, label: 'Score 4,500' }],
    layout: holes(8, 8, [
      [0, 3], [0, 4], [3, 0], [4, 0], [3, 7], [4, 7], [7, 3], [7, 4],
    ]),
    difficulty: 3,
    seed: 3021,
  }),
  L({
    id: 22,
    worldId: 3,
    name: 'Portal Twin',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'collect', target: 22, kind: 'gale', label: 'Collect 22 Gale Plumes' },
    ],
    layout: {
      ...portalPair(1, 1, 6, 6),
      ...portalPair(1, 6, 6, 1),
    },
    difficulty: 3,
    seed: 3022,
    tutorial: ['Portals teleport falling essences to linked cells.'],
  }),
  L({
    id: 23,
    worldId: 3,
    name: 'One-Way Draft',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [{ type: 'score', target: 5000, label: 'Score 5,000' }],
    layout: oneWayRow(3, 8, 'right'),
    difficulty: 3,
    seed: 3023,
    tutorial: ['One-way cells only allow swaps in the marked direction.'],
  }),
  L({
    id: 24,
    worldId: 3,
    name: 'Conveyor Belt',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'collect', target: 20, kind: 'solar', label: 'Collect 20 Solar Crests' },
      { type: 'clear_frost', target: 10, label: 'Melt 10 Frost' },
    ],
    layout: {
      ...conveyorRow(4, 8, 'left'),
      ...frostPatch(8, 8, [[5, 1], [5, 2], [5, 5], [5, 6], [6, 3], [6, 4], [2, 2], [2, 5], [1, 4], [1, 3]], 1),
    },
    difficulty: 3,
    seed: 3024,
    tutorial: ['Conveyors shift essences after each turn — plan around the drift.'],
  }),
  L({
    id: 25,
    worldId: 3,
    name: 'Spire Narrows',
    moves: 18,
    rows: 9,
    cols: 6,
    pieceSet: SIX,
    objectives: [
      { type: 'activate_specials', target: 3, label: 'Forge 3 Specials' },
      { type: 'score', target: 6000, label: 'Score 6,000' },
    ],
    difficulty: 3,
    seed: 3025,
  }),
  L({
    id: 26,
    worldId: 3,
    name: 'Sealed Gusts',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [{ type: 'clear_seals', target: 8, label: 'Crack 8 Seals' }],
    layout: seals(8, 8, [
      [2, 2], [2, 5], [3, 3], [3, 4], [4, 3], [4, 4], [5, 2], [5, 5],
    ]),
    difficulty: 3,
    seed: 3026,
    tutorial: ['Sealed cells cannot be swapped until cracked by a match or special.'],
  }),
  L({
    id: 27,
    worldId: 3,
    name: 'Crosswinds',
    moves: 22,
    rows: 9,
    cols: 9,
    pieceSet: SIX,
    objectives: [
      { type: 'clear_tiles', target: 20, label: 'Restore 20 Loom Tiles' },
      { type: 'combo_specials', target: 1, label: 'Combine Specials once' },
    ],
    layout: {
      ...underTiles(9, 9, ringCoords(9, 9, 2), 1),
      ...holes(9, 9, [[4, 4]]),
      ...portalPair(0, 4, 8, 4),
    },
    difficulty: 4,
    seed: 3027,
  }),
  L({
    id: 28,
    worldId: 3,
    name: 'Beam Storm',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'activate_specials', target: 4, special: 'beam', label: 'Forge 4 Beam Spindles' },
    ],
    difficulty: 4,
    seed: 3028,
  }),
  L({
    id: 29,
    worldId: 3,
    name: 'Asymmetric Loom',
    moves: 22,
    rows: 9,
    cols: 8,
    pieceSet: ALL.slice(0, 6),
    objectives: [
      { type: 'score', target: 7000, label: 'Score 7,000' },
      { type: 'clear_frost', target: 14, label: 'Melt 14 Frost' },
    ],
    layout: {
      ...holes(9, 8, [[0, 0], [0, 1], [1, 0], [8, 6], [8, 7], [7, 7]]),
      ...frostPatch(9, 8, fullRect(3, 2, 6, 5), 2),
    },
    difficulty: 4,
    seed: 3029,
  }),
  L({
    id: 30,
    worldId: 3,
    name: 'Gale Archon',
    moves: 25,
    rows: 9,
    cols: 9,
    pieceSet: SIX,
    objectives: [
      { type: 'drop', target: 3, label: 'Drop 3 Star Relics' },
      { type: 'clear_seals', target: 6, label: 'Crack 6 Seals' },
      { type: 'activate_specials', target: 4, label: 'Forge 4 Specials' },
    ],
    layout: {
      ...seals(9, 9, [[2, 2], [2, 6], [4, 4], [6, 2], [6, 6], [3, 5]]),
      ...portalPair(1, 4, 7, 4),
      ...conveyorRow(5, 9, 'right'),
    },
    prefill: relicTop(9, 3),
    difficulty: 4,
    seed: 3030,
  }),

  // ─── World 4: Root Sanctum — vines, crates, layered ───
  L({
    id: 31,
    worldId: 4,
    name: 'Living Vine',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [{ type: 'clear_vine', target: 8, label: 'Clear 8 Vine patches' }],
    layout: vines(8, 8, [[3, 3], [3, 4], [4, 3], [4, 4], [2, 5], [5, 2]]),
    difficulty: 3,
    seed: 4031,
    tutorial: ['Living Vines can spread if left alone. Match on them to prune.'],
  }),
  L({
    id: 32,
    worldId: 4,
    name: 'Deep Roots',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'clear_crate', target: 16, label: 'Break 16 Crate layers' },
      { type: 'collect', target: 20, kind: 'root', label: 'Collect 20 Root Knots' },
    ],
    layout: crates(8, 8, fullRect(2, 2, 5, 5), 2),
    difficulty: 3,
    seed: 4032,
  }),
  L({
    id: 33,
    worldId: 4,
    name: 'Vine & Frost',
    moves: 24,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'clear_vine', target: 10, label: 'Clear 10 Vines' },
      { type: 'clear_frost', target: 12, label: 'Melt 12 Frost' },
    ],
    layout: {
      ...vines(8, 8, [[1, 1], [1, 6], [6, 1], [6, 6], [3, 3], [4, 4]]),
      ...frostPatch(8, 8, [[2, 3], [2, 4], [5, 3], [5, 4], [3, 2], [4, 2], [3, 5], [4, 5], [0, 3], [0, 4], [7, 3], [7, 4]], 1),
    },
    difficulty: 4,
    seed: 4033,
  }),
  L({
    id: 34,
    worldId: 4,
    name: 'Locked Grove',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: ALL.slice(0, 6),
    objectives: [
      { type: 'clear_seals', target: 10, label: 'Crack 10 Seals' },
      { type: 'score', target: 5500, label: 'Score 5,500' },
    ],
    layout: seals(8, 8, checker(8, 8).slice(0, 12)),
    difficulty: 4,
    seed: 4034,
  }),
  L({
    id: 35,
    worldId: 4,
    name: 'Heavy Crates',
    moves: 24,
    rows: 9,
    cols: 8,
    pieceSet: SIX,
    objectives: [{ type: 'clear_crate', target: 24, label: 'Break 24 Crate layers' }],
    layout: crates(9, 8, fullRect(1, 2, 7, 5), 3),
    difficulty: 4,
    seed: 4035,
  }),
  L({
    id: 36,
    worldId: 4,
    name: 'Triple Threat',
    moves: 24,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'clear_vine', target: 8, label: 'Clear 8 Vines' },
      { type: 'clear_crate', target: 10, label: 'Break 10 Crates' },
      { type: 'clear_tiles', target: 14, label: 'Restore 14 Tiles' },
    ],
    layout: {
      ...vines(8, 8, [[2, 2], [2, 5], [5, 2], [5, 5]]),
      ...crates(8, 8, [[3, 3], [3, 4], [4, 3], [4, 4], [1, 4], [6, 3]], 2),
      ...underTiles(8, 8, fullRect(2, 2, 5, 5), 1),
    },
    difficulty: 4,
    seed: 4036,
  }),
  L({
    id: 37,
    worldId: 4,
    name: 'Narrow Roots',
    moves: 18,
    rows: 9,
    cols: 5,
    pieceSet: BASIC,
    objectives: [
      { type: 'score', target: 6000, label: 'Score 6,000' },
      { type: 'activate_specials', target: 3, label: 'Forge 3 Specials' },
    ],
    difficulty: 4,
    seed: 4037,
  }),
  L({
    id: 38,
    worldId: 4,
    name: 'Sanctum Maze',
    moves: 24,
    rows: 9,
    cols: 9,
    pieceSet: SIX,
    objectives: [
      { type: 'drop', target: 2, label: 'Drop 2 Star Relics' },
      { type: 'clear_vine', target: 12, label: 'Clear 12 Vines' },
    ],
    layout: {
      ...holes(9, 9, [[2, 2], [2, 6], [6, 2], [6, 6], [4, 4]]),
      ...vines(9, 9, ringCoords(9, 9, 3).slice(0, 10)),
    },
    prefill: relicTop(9, 2),
    difficulty: 4,
    seed: 4038,
  }),
  L({
    id: 39,
    worldId: 4,
    name: 'Prism Orchard',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: SIX,
    objectives: [
      { type: 'activate_specials', target: 2, special: 'prism', label: 'Forge 2 Prism Hearts' },
      { type: 'collect', target: 30, kind: 'ember', label: 'Collect 30 Ember Petals' },
    ],
    difficulty: 4,
    seed: 4039,
  }),
  L({
    id: 40,
    worldId: 4,
    name: 'Root Colossus',
    moves: 26,
    rows: 9,
    cols: 9,
    pieceSet: ALL.slice(0, 6),
    objectives: [
      { type: 'clear_crate', target: 18, label: 'Break 18 Crate layers' },
      { type: 'clear_vine', target: 10, label: 'Clear 10 Vines' },
      { type: 'clear_frost', target: 12, label: 'Melt 12 Frost' },
      { type: 'combo_specials', target: 1, label: 'Combine Specials' },
    ],
    layout: {
      ...crates(9, 9, [[2, 4], [3, 3], [3, 5], [4, 2], [4, 6], [5, 3], [5, 5], [6, 4], [4, 4]], 2),
      ...vines(9, 9, [[1, 1], [1, 7], [7, 1], [7, 7], [0, 4], [8, 4]]),
      ...frostPatch(9, 9, fullRect(3, 3, 5, 5), 2),
    },
    difficulty: 5,
    seed: 4040,
  }),

  // ─── World 5: Void Observatory — hazards, complex ───
  L({
    id: 41,
    worldId: 5,
    name: 'Void Introduction',
    moves: 20,
    rows: 8,
    cols: 8,
    pieceSet: ALL,
    objectives: [
      { type: 'collect', target: 16, kind: 'void', label: 'Collect 16 Void Prisms' },
      { type: 'score', target: 5000, label: 'Score 5,000' },
    ],
    difficulty: 3,
    seed: 5041,
    tutorial: ['Void Prisms join the palette — seven essences demand careful reading.'],
  }),
  L({
    id: 42,
    worldId: 5,
    name: 'Rift Edge',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: ALL,
    objectives: [
      { type: 'survive_rift', target: 3, label: 'Contain Rifts (≤3)' },
      { type: 'score', target: 4500, label: 'Score 4,500' },
    ],
    layout: rifts(8, 8, [[3, 3], [4, 4]]),
    difficulty: 4,
    seed: 5042,
    tutorial: ['Rifts are unstable hazards. Keep their number under control while you score.'],
  }),
  L({
    id: 43,
    worldId: 5,
    name: 'Portal Labyrinth',
    moves: 24,
    rows: 9,
    cols: 9,
    pieceSet: ALL,
    objectives: [
      { type: 'clear_tiles', target: 28, label: 'Restore 28 Loom Tiles' },
      { type: 'activate_specials', target: 4, label: 'Forge 4 Specials' },
    ],
    layout: {
      ...underTiles(9, 9, fullRect(0, 0, 8, 8), 1),
      ...portalPair(0, 0, 8, 8),
      ...portalPair(0, 8, 8, 0),
      ...holes(9, 9, [[4, 4]]),
    },
    difficulty: 4,
    seed: 5043,
  }),
  L({
    id: 44,
    worldId: 5,
    name: 'Combo Mandate',
    moves: 24,
    rows: 8,
    cols: 8,
    pieceSet: ALL,
    objectives: [
      { type: 'combo_specials', target: 2, label: 'Combine Specials twice' },
      { type: 'score', target: 8000, label: 'Score 8,000' },
    ],
    difficulty: 4,
    seed: 5044,
  }),
  L({
    id: 45,
    worldId: 5,
    name: 'Pressure Clock',
    moves: 14,
    rows: 8,
    cols: 8,
    pieceSet: ALL,
    objectives: [
      { type: 'score', target: 6000, label: 'Score 6,000' },
      { type: 'collect', target: 12, kind: 'solar', label: 'Collect 12 Solar Crests' },
    ],
    difficulty: 5,
    seed: 5045,
  }),
  L({
    id: 46,
    worldId: 5,
    name: 'Layered Chaos',
    moves: 26,
    rows: 9,
    cols: 9,
    pieceSet: ALL,
    objectives: [
      { type: 'clear_frost', target: 18, label: 'Melt 18 Frost' },
      { type: 'clear_crate', target: 12, label: 'Break 12 Crates' },
      { type: 'clear_vine', target: 8, label: 'Clear 8 Vines' },
    ],
    layout: {
      ...frostPatch(9, 9, fullRect(1, 1, 7, 7), 2),
      ...crates(9, 9, [[2, 4], [4, 2], [4, 6], [6, 4], [3, 3], [5, 5]], 2),
      ...vines(9, 9, [[1, 4], [4, 1], [4, 7], [7, 4]]),
    },
    difficulty: 5,
    seed: 5046,
  }),
  L({
    id: 47,
    worldId: 5,
    name: 'Conveyor Void',
    moves: 22,
    rows: 8,
    cols: 8,
    pieceSet: ALL,
    objectives: [
      { type: 'drop', target: 3, label: 'Drop 3 Star Relics' },
      { type: 'clear_seals', target: 6, label: 'Crack 6 Seals' },
    ],
    layout: {
      ...conveyorRow(2, 8, 'right'),
      ...conveyorRow(5, 8, 'left'),
      ...seals(8, 8, [[3, 1], [3, 6], [4, 2], [4, 5], [6, 3], [1, 4]]),
    },
    prefill: relicTop(8, 3),
    difficulty: 5,
    seed: 5047,
  }),
  L({
    id: 48,
    worldId: 5,
    name: 'Sevenfold',
    moves: 24,
    rows: 9,
    cols: 9,
    pieceSet: ALL,
    objectives: [
      { type: 'collect', target: 12, kind: 'ember', label: '12 Ember' },
      { type: 'collect', target: 12, kind: 'tide', label: '12 Tide' },
      { type: 'collect', target: 12, kind: 'void', label: '12 Void' },
    ],
    difficulty: 5,
    seed: 5048,
  }),
  L({
    id: 49,
    worldId: 5,
    name: 'Observatory Trial',
    moves: 26,
    rows: 9,
    cols: 9,
    pieceSet: ALL,
    objectives: [
      { type: 'activate_specials', target: 2, special: 'prism', label: 'Forge 2 Prism Hearts' },
      { type: 'combo_specials', target: 2, label: '2 Special Combos' },
      { type: 'clear_tiles', target: 20, label: 'Restore 20 Tiles' },
    ],
    layout: {
      ...underTiles(9, 9, ringCoords(9, 9, 1), 1),
      ...portalPair(1, 1, 7, 7),
      ...rifts(9, 9, [[4, 4]]),
    },
    difficulty: 5,
    seed: 5049,
  }),
  L({
    id: 50,
    worldId: 5,
    name: 'Aetherloom Apex',
    moves: 28,
    rows: 9,
    cols: 9,
    pieceSet: ALL,
    objectives: [
      { type: 'score', target: 12000, label: 'Score 12,000' },
      { type: 'drop', target: 3, label: 'Drop 3 Star Relics' },
      { type: 'clear_crate', target: 12, label: 'Break 12 Crates' },
      { type: 'combo_specials', target: 2, label: '2 Special Combos' },
      { type: 'clear_vine', target: 8, label: 'Clear 8 Vines' },
    ],
    layout: {
      ...crates(9, 9, [[2, 2], [2, 6], [6, 2], [6, 6], [3, 4], [5, 4]], 2),
      ...vines(9, 9, [[1, 4], [4, 1], [4, 7], [7, 4]]),
      ...frostPatch(9, 9, [[4, 3], [4, 5], [3, 4], [5, 4]], 2),
      ...portalPair(0, 4, 8, 4),
      ...seals(9, 9, [[2, 4], [6, 4]]),
    },
    prefill: relicTop(9, 3),
    difficulty: 5,
    seed: 5050,
  }),
];

export const WORLDS: WorldDef[] = [
  {
    id: 1,
    name: 'Ember Meadows',
    subtitle: 'Learn the loom among warm petals',
    theme: 'ember',
    levelIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
  {
    id: 2,
    name: 'Tide Atelier',
    subtitle: 'Currents, relics, and luminous tiles',
    theme: 'tide',
    levelIds: [11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
    unlockAfter: 10,
  },
  {
    id: 3,
    name: 'Gale Spires',
    subtitle: 'Portals, drafts, and narrow winds',
    theme: 'gale',
    levelIds: [21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
    unlockAfter: 20,
  },
  {
    id: 4,
    name: 'Root Sanctum',
    subtitle: 'Vines, crates, and deep growth',
    theme: 'root',
    levelIds: [31, 32, 33, 34, 35, 36, 37, 38, 39, 40],
    unlockAfter: 30,
  },
  {
    id: 5,
    name: 'Void Observatory',
    subtitle: 'Seven essences under starlight',
    theme: 'void',
    levelIds: [41, 42, 43, 44, 45, 46, 47, 48, 49, 50],
    unlockAfter: 40,
  },
];

export function getLevel(id: number): LevelDef | undefined {
  return CAMPAIGN.find((l) => l.id === id);
}

export function getWorld(id: number): WorldDef | undefined {
  return WORLDS.find((w) => w.id === id);
}

// ─── layout helpers ───

function frostPatch(
  _rows: number,
  _cols: number,
  coords: number[][],
  layers: number
): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) {
    layout[`${r},${c}`] = { blocker: { type: 'frost', layers }, under: undefined };
    // frost as under-style: store as blocker frost
    layout[`${r},${c}`] = { blocker: { type: 'frost', layers } };
  }
  return layout;
}

function underTiles(
  _rows: number,
  _cols: number,
  coords: number[][],
  layers: number
): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) {
    layout[`${r},${c}`] = { under: { layers } };
  }
  return layout;
}

function holes(_rows: number, _cols: number, coords: number[][]): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) layout[`${r},${c}`] = { hole: true };
  return layout;
}

function crates(
  _rows: number,
  _cols: number,
  coords: number[][],
  layers: number
): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) layout[`${r},${c}`] = { blocker: { type: 'crate', layers } };
  return layout;
}

function vines(_rows: number, _cols: number, coords: number[][]): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) layout[`${r},${c}`] = { blocker: { type: 'vine', layers: 1 } };
  return layout;
}

function seals(_rows: number, _cols: number, coords: number[][]): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) layout[`${r},${c}`] = { sealed: true, blocker: { type: 'seal', layers: 1 } };
  return layout;
}

function rifts(_rows: number, _cols: number, coords: number[][]): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (const [r, c] of coords) layout[`${r},${c}`] = { blocker: { type: 'rift', layers: 1 } };
  return layout;
}

function portalPair(r1: number, c1: number, r2: number, c2: number): LevelDef['layout'] {
  return {
    [`${r1},${c1}`]: { portalTo: { r: r2, c: c2 } },
    [`${r2},${c2}`]: { portalTo: { r: r1, c: c1 } },
  };
}

function conveyorRow(row: number, cols: number, dir: 'left' | 'right'): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (let c = 0; c < cols; c++) layout[`${row},${c}`] = { conveyorDir: dir };
  return layout;
}

function oneWayRow(row: number, cols: number, dir: 'left' | 'right' | 'up' | 'down'): LevelDef['layout'] {
  const layout: NonNullable<LevelDef['layout']> = {};
  for (let c = 0; c < cols; c++) layout[`${row},${c}`] = { oneWay: dir };
  return layout;
}

function fullRect(r0: number, c0: number, r1: number, c1: number): number[][] {
  const out: number[][] = [];
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) out.push([r, c]);
  return out;
}

function checker(rows: number, cols: number): number[][] {
  const out: number[][] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) if ((r + c) % 2 === 0) out.push([r, c]);
  return out;
}

function ringCoords(rows: number, cols: number, inset: number): number[][] {
  const out: number[][] = [];
  for (let r = inset; r < rows - inset; r++) {
    for (let c = inset; c < cols - inset; c++) {
      if (
        r === inset ||
        c === inset ||
        r === rows - inset - 1 ||
        c === cols - inset - 1
      ) {
        out.push([r, c]);
      }
    }
  }
  return out;
}

function relicTop(cols: number, count: number): LevelDef['prefill'] {
  const prefill: NonNullable<LevelDef['prefill']> = {};
  const kinds: PieceKind[] = ['solar', 'moon', 'void', 'ember', 'tide'];
  for (let i = 0; i < count; i++) {
    const c = Math.floor(((i + 0.5) * cols) / count);
    prefill[`0,${Math.min(cols - 1, c)}`] = { kind: kinds[i % kinds.length], isRelic: true };
  }
  return prefill;
}
